import React, { useCallback, useEffect, useState, useMemo } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  DashboardLayout,
  Breadcrumb,
  getSidebarBrandLogoNodes,
} from "@ippon/shared-layouts";
import type {
  SidebarMenuItem,
  RouteEntry as BreadcrumbRouteEntry,
} from "@ippon/shared-layouts";
import type { LogoMenuItem } from "@ippon/shared-layouts/src/Sidebar";
import { useMediaQuery, useTheme, Fab, Tooltip, Box } from "@mui/material";
import { AutoAwesomeOutlined as AutoAwesomeOutlinedIcon } from "@mui/icons-material";
import {
  profileImg,
  logosPng,
  ipponTradingLogoSpXs,
  ExternalLinkIcon,
} from "@ippon/shared-assets";
import {
  getFullName,
  getEmployeeID,
  getMyUseServiceCookie,
  setMyUseServiceCookie,
  clearPostLoginRedirect,
} from "../../utils/authUtils";
import ipponApi from "../../config/axios";
import AIChatButton from "../../components/AIChatbot/AIChatButton";
import {
  ChatRightPane,
  type ChatDisplayMode,
} from "../../components/ChatRightPane";
import {
  DEFAULT_CHAT_PANE_OPEN,
  shouldShowChatReopenButton,
} from "./chatReopenVisibility";
import {
  resolveChatPanePresentation,
  shouldMountDesktopChatPane,
} from "./chatPanePresentation";
import { shouldCloseChatPaneOnNavigation } from "./chatPaneNavigation";
import { useResizableChatPane } from "./useResizableChatPane";

import { useSelector, useDispatch } from "react-redux";
import { logoutAPI } from "../../store/authSlice";
import { setSidebarOpen, setSidebarCollapsed } from "../../store/sidebarSlice";
import { fetchDetailedClient } from "../../store/clientSlice";
import { getClientID } from "../../utils/authUtils";
import type { AppDispatch, RootState } from "../../store";
import {
  DEFAULT_EXPENSE_MANAGEMENT_SUBTAB_ID,
  DEFAULT_PROPERTY_DETAIL_TAB_ID,
  EXPENSE_MANAGEMENT_SUBTABS,
  PROPERTY_DETAIL_TAB_IDS as PROPERTY_TABS,
  PROPERTY_DETAIL_TABS,
  getTabLabel,
  isValidPropertyDetailTabQuery,
  resolveTabId,
} from "../../components/PropertyDetail/tabConfig";
import {
  buildCanonicalEstimateBreadcrumbs,
  buildCanonicalExpenseBreadcrumbs,
  buildCanonicalPaymentBreadcrumbs,
  buildCanonicalPropertyBreadcrumbs,
  buildCanonicalRepairBreadcrumbs,
  buildCanonicalUnitBreadcrumbs,
} from "./breadcrumbHierarchy";

const BreadcrumbPropertyIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g clipPath="url(#clip0_21087_34961)">
      <path
        d="M6 11.3333H5.33333C5.33333 11.5101 5.40357 11.6797 5.5286 11.8047C5.65362 11.9298 5.82319 12 6 12V11.3333ZM11.3333 11.3333V12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333H11.3333ZM11.3333 8H12C12.0002 7.91239 11.983 7.82561 11.9496 7.74463C11.9162 7.66364 11.8672 7.59002 11.8053 7.528L11.3333 8ZM8.66667 5.33333L9.13867 4.86133C9.07674 4.79925 9.00317 4.74999 8.92218 4.71638C8.84118 4.68277 8.75436 4.66547 8.66667 4.66547C8.57898 4.66547 8.49215 4.68277 8.41115 4.71638C8.33016 4.74999 8.25659 4.79925 8.19467 4.86133L8.66667 5.33333ZM6 8L5.528 7.528C5.46613 7.59002 5.41709 7.66364 5.38369 7.74463C5.35029 7.82561 5.33318 7.91239 5.33333 8H6ZM19.8053 18.8613L14.472 13.528L13.528 14.472L18.8613 19.8053L19.8053 18.8613ZM8.66667 16C6.72175 16 4.85648 15.2274 3.48122 13.8521C2.10595 12.4768 1.33333 10.6116 1.33333 8.66667H0C0 10.9652 0.913093 13.1696 2.53841 14.7949C4.16372 16.4202 6.36812 17.3333 8.66667 17.3333V16ZM16 8.66667C16 10.6116 15.2274 12.4768 13.8521 13.8521C12.4768 15.2274 10.6116 16 8.66667 16V17.3333C10.9652 17.3333 13.1696 16.4202 14.7949 14.7949C16.4202 13.1696 17.3333 10.9652 17.3333 8.66667H16ZM8.66667 1.33333C10.6116 1.33333 12.4768 2.10595 13.8521 3.48122C15.2274 4.85648 16 6.72175 16 8.66667H17.3333C17.3333 6.36812 16.4202 4.16372 14.7949 2.53841C13.1696 0.913093 10.9652 0 8.66667 0V1.33333ZM8.66667 0C6.36812 0 4.16372 0.913093 2.53841 2.53841C0.913093 4.16372 0 6.36812 0 8.66667H1.33333C1.33333 6.72175 2.10595 4.85648 3.48122 3.48122C4.85648 2.10595 6.72175 1.33333 8.66667 1.33333V0ZM6 12H11.3333V10.6667H6V12ZM12 11.3333V8H10.6667V11.3333H12ZM11.8053 7.528L9.13867 4.86133L8.19467 5.80533L10.8613 8.472L11.8053 7.528ZM8.19467 4.86133L5.528 7.528L6.472 8.472L9.13867 5.80533L8.19467 4.86133ZM5.33333 8V11.3333H6.66667V8H5.33333Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_21087_34961">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const getClientSettingUrl = (): string => {
  const hostname = window.location.hostname;
  if (hostname.includes("stg.ippon-cloud.com")) {
    return "https://client-setting.stg.ippon-cloud.com";
  }
  if (hostname.includes("ippon-cloud.com")) {
    return "https://client-setting.ippon-cloud.com";
  }
  return "http://localhost:5173";
};

// サイドバーメニュー設定
const sidebarMenus: SidebarMenuItem[] = [
  {
    id: "home",
    label: "ホーム",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 10.5 12 3l9 7.5M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    subItems: [{ to: "/home", label: "ホーム" }],
  },
  {
    id: "properties",
    label: "物件一覧",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_21087_34961)">
          <path
            d="M6 11.3333H5.33333C5.33333 11.5101 5.40357 11.6797 5.5286 11.8047C5.65362 11.9298 5.82319 12 6 12V11.3333ZM11.3333 11.3333V12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333H11.3333ZM11.3333 8H12C12.0002 7.91239 11.983 7.82561 11.9496 7.74463C11.9162 7.66364 11.8672 7.59002 11.8053 7.528L11.3333 8ZM8.66667 5.33333L9.13867 4.86133C9.07674 4.79925 9.00317 4.74999 8.92218 4.71638C8.84118 4.68277 8.75436 4.66547 8.66667 4.66547C8.57898 4.66547 8.49215 4.68277 8.41115 4.71638C8.33016 4.74999 8.25659 4.79925 8.19467 4.86133L8.66667 5.33333ZM6 8L5.528 7.528C5.46613 7.59002 5.41709 7.66364 5.38369 7.74463C5.35029 7.82561 5.33318 7.91239 5.33333 8H6ZM19.8053 18.8613L14.472 13.528L13.528 14.472L18.8613 19.8053L19.8053 18.8613ZM8.66667 16C6.72175 16 4.85648 15.2274 3.48122 13.8521C2.10595 12.4768 1.33333 10.6116 1.33333 8.66667H0C0 10.9652 0.913093 13.1696 2.53841 14.7949C4.16372 16.4202 6.36812 17.3333 8.66667 17.3333V16ZM16 8.66667C16 10.6116 15.2274 12.4768 13.8521 13.8521C12.4768 15.2274 10.6116 16 8.66667 16V17.3333C10.9652 17.3333 13.1696 16.4202 14.7949 14.7949C16.4202 13.1696 17.3333 10.9652 17.3333 8.66667H16ZM8.66667 1.33333C10.6116 1.33333 12.4768 2.10595 13.8521 3.48122C15.2274 4.85648 16 6.72175 16 8.66667H17.3333C17.3333 6.36812 16.4202 4.16372 14.7949 2.53841C13.1696 0.913093 10.9652 0 8.66667 0V1.33333ZM8.66667 0C6.36812 0 4.16372 0.913093 2.53841 2.53841C0.913093 4.16372 0 6.36812 0 8.66667H1.33333C1.33333 6.72175 2.10595 4.85648 3.48122 3.48122C4.85648 2.10595 6.72175 1.33333 8.66667 1.33333V0ZM6 12H11.3333V10.6667H6V12ZM12 11.3333V8H10.6667V11.3333H12ZM11.8053 7.528L9.13867 4.86133L8.19467 5.80533L10.8613 8.472L11.8053 7.528ZM8.19467 4.86133L5.528 7.528L6.472 8.472L9.13867 5.80533L8.19467 4.86133ZM5.33333 8V11.3333H6.66667V8H5.33333Z"
            fill="currentColor"
          />
        </g>
        <defs>
          <clipPath id="clip0_21087_34961">
            <rect width="20" height="20" fill="white" />
          </clipPath>
        </defs>
      </svg>
    ),
    subItems: [{ to: "/properties", label: "物件一覧" }],
  },
  {
    id: "budget-analysis",
    label: "予実分析",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 3v18h18M7 14l4-4 4 4 5-5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    subItems: [{ to: "/budget-analysis", label: "予実分析" }],
  },
  {
    id: "estimates",
    label: "見積書一覧",
    icon: (
      <svg
        width="13"
        height="16"
        viewBox="0 0 13 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.96144 8.14428H9.28856V7.26928H2.96144V8.14428ZM2.96144 10.5674H9.28856V9.69237H2.96144V10.5674ZM2.96144 12.9905H6.66356V12.1155H2.96144V12.9905ZM1.41356 15.75C1.01077 15.75 0.674479 15.6151 0.404687 15.3453C0.134896 15.0755 0 14.7392 0 14.3364V1.41356C0 1.01077 0.134896 0.674479 0.404687 0.404687C0.674479 0.134896 1.01077 0 1.41356 0H8.3125L12.25 3.9375V14.3364C12.25 14.7392 12.1151 15.0755 11.8453 15.3453C11.5755 15.6151 11.2392 15.75 10.8364 15.75H1.41356ZM7.875 4.375V0.875H1.41356C1.27881 0.875 1.15536 0.931073 1.04322 1.04322C0.931073 1.15536 0.875 1.27881 0.875 1.41356V14.3364C0.875 14.4712 0.931073 14.5946 1.04322 14.7068C1.15536 14.8189 1.27881 14.875 1.41356 14.875H10.8364C10.9712 14.875 11.0946 14.8189 11.2068 14.7068C11.3189 14.5946 11.375 14.4712 11.375 14.3364V4.375H7.875Z"
          fill="currentColor"
        />
      </svg>
    ),
    subItems: [
      { to: "/estimates", label: "見積書一覧" },
      // { to: "/estimate/create", label: "新規顧客登録" },
    ],
  },
  {
    id: "setting",
    label: "設定",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    forceSubmenu: true,
    subItems: [
      { to: "/setting/system", label: "書類設定" },
      { to: "/setting/custom", label: "カスタム設定" },
      { to: "/setting/fiscal-closing", label: "決算月" },
      {
        to: `${getClientSettingUrl()}/setting/account`,
        label: "アカウント管理",
        external: true,
        icon: (
          <span
            style={{
              marginLeft: "2px",
              display: "inline-flex",
              position: "relative",
              top: "-0.5px",
            }}
          >
            <ExternalLinkIcon />
          </span>
        ),
      },
    ],
  },
];

// アカウント管理メニュー（削除）
const bottomMenus: SidebarMenuItem[] = [];

// パンくずリスト設定

const routeConfig: Record<string, BreadcrumbRouteEntry> = {
  "/": {
    breadcrumb: [],
  },
  "/home": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 10.5 12 3l9 7.5M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    breadcrumb: [{ path: "/home", title: "ホーム" }],
  },
  "/properties": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: [{ path: "/properties", title: "物件一覧" }],
  },
  "/budget-analysis": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 3v18h18M7 14l4-4 4 4 5-5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    breadcrumb: [{ path: "/budget-analysis", title: "予実分析" }],
  },
  "/budget-analysis/group/:groupName": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M3 3v18h18M7 14l4-4 4 4 5-5"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
    breadcrumb: ({ params }: any) => [
      { path: "/budget-analysis", title: "予実分析" },
      {
        path: `/budget-analysis/group/${params?.groupName ?? ""}`,
        title: decodeURIComponent(params?.groupName ?? ""),
      },
    ],
  },
  "/properties/create": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: [
      { path: "/properties", title: "物件一覧" },
      { path: "/properties/create", title: "物件登録" },
    ],
  },

  "/properties/:id": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({ params, propName, searchParams }: any) => {
      const breadcrumbs = buildCanonicalPropertyBreadcrumbs({
        propertyId: params?.id || "",
        propertyName: propName ?? "",
      });

      const hasValidTabQuery = isValidPropertyDetailTabQuery(
        new URLSearchParams(searchParams?.toString?.() ?? ""),
      );

      const tabParam = searchParams?.get?.("tab");
      const tabIndex =
        tabParam == null
          ? DEFAULT_PROPERTY_DETAIL_TAB_ID
          : resolveTabId(tabParam, PROPERTY_DETAIL_TABS);
      const tabLabel =
        hasValidTabQuery && tabIndex != null
          ? getTabLabel(PROPERTY_DETAIL_TABS, tabIndex)
          : undefined;

      if (tabLabel && tabIndex != null) {
        breadcrumbs.push({
          path: `/properties/${params?.id}?tab=${tabIndex}`,
          title: tabLabel,
        });
      }

      // 収支 (tab=5) のサブタブをパンくずに追加
      if (hasValidTabQuery && tabIndex === PROPERTY_TABS.MANAGEMENT) {
        const subtabParam = searchParams?.get?.("subtab");
        const subtabIndex =
          subtabParam == null
            ? DEFAULT_EXPENSE_MANAGEMENT_SUBTAB_ID
            : resolveTabId(subtabParam, EXPENSE_MANAGEMENT_SUBTABS);
        const subtabLabel =
          subtabIndex == null
            ? undefined
            : getTabLabel(EXPENSE_MANAGEMENT_SUBTABS, subtabIndex);
        if (subtabLabel && subtabIndex != null) {
          breadcrumbs.push({
            path: `/properties/${params?.id}?tab=${PROPERTY_TABS.MANAGEMENT}&subtab=${subtabIndex}`,
            title: subtabLabel,
          });
        }
      }

      return breadcrumbs;
    },
  },
  // 区画詳細は遷移元に関係なく、本来の所属である物件構成配下に置く。
  "/properties/:propertyId/units/:unitId": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({ params, propName, customerName }: any) =>
      buildCanonicalUnitBreadcrumbs({
        propertyId: params?.propertyId || "",
        propertyName: propName ?? "物件名",
        unitId: params?.unitId || "",
        unitName: customerName || params?.unitId || "詳細情報",
      }),
  },
  "/repairs/:id": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({ params, propName, customerName, propertyId }: any) =>
      buildCanonicalRepairBreadcrumbs({
        propertyId: propertyId || params?.propertyId || "",
        propertyName: propName ?? "物件名",
        repairId: params?.id || "",
        repairTitle: customerName ?? "",
      }),
  },
  "/management-tasks/:id": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({ params, propName, customerName, searchParams }: any) => {
      const pid = searchParams?.get?.("property_id") || "";

      const base: Array<{
        path: string;
        title: string;
        truncatable?: boolean;
      }> = [{ path: "/properties", title: "物件一覧" }];

      if (pid) {
        base.push({
          path: `/properties/${pid}`,
          title: propName ?? "",
          truncatable: true,
        });
        base.push({
          path: `/properties/${pid}?tab=${PROPERTY_TABS.INSPECTION}`,
          title: "点検/清掃",
        });
      }

      // customerName は ManagementTaskDetailPage が data.title をロード後に
      // state.title へ詰める。ロード完了までは最終項目を出さずチラつき防止。
      if (customerName) {
        base.push({
          path: `/management-tasks/${params?.id}`,
          title: customerName,
          truncatable: true,
        });
      }

      return base;
    },
  },
  "/management-task-results/:task_id": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({ params, propName, customerName, searchParams }: any) => {
      const pid = searchParams?.get?.("property_id") || "";
      const year = searchParams?.get?.("year");
      const month = searchParams?.get?.("month");
      // location.state.taskName を navigation 時に詰めるので window.history から取得
      const usr =
        (typeof window !== "undefined"
          ? (window as any)?.history?.state?.usr
          : null) ?? null;
      const taskName: string =
        (usr && typeof usr.taskName === "string" && usr.taskName) || "";

      const base: Array<{
        path: string;
        title: string;
        truncatable?: boolean;
      }> = [{ path: "/properties", title: "物件一覧" }];

      if (pid) {
        base.push({
          path: `/properties/${pid}`,
          title: propName ?? "",
          truncatable: true,
        });
        base.push({
          path: `/properties/${pid}?tab=${PROPERTY_TABS.INSPECTION}`,
          title: "点検/清掃",
        });
        // タスク本体への戻り (querystring の property_id を引き継ぎ)
        if (params?.task_id && taskName) {
          base.push({
            path: `/management-tasks/${params.task_id}?property_id=${encodeURIComponent(pid)}`,
            title: taskName,
            truncatable: true,
          });
        }
      }

      // customerName = "YYYY年MM月" (page が state.title に詰める)。
      // 念のため year/month が querystring にあれば fallback で組成。
      const monthTitle =
        customerName ||
        (year && month ? `${year}年${String(month).padStart(2, "0")}月` : null);
      if (monthTitle) {
        base.push({
          path: `/management-task-results/${params?.task_id}`,
          title: monthTitle,
          truncatable: true,
        });
      }

      return base;
    },
  },
  "/payments/:id": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({
      params,
      propName,
      customerName,
      searchParams,
      privateAreaId,
      privateAreaName,
    }: any) => {
      const pid = searchParams?.get?.("property_id") || "";
      const sid =
        searchParams?.get?.("property_section_id") || privateAreaId || "";
      return buildCanonicalPaymentBreadcrumbs({
        propertyId: pid,
        propertyName: propName ?? "",
        unitId: sid,
        unitName: privateAreaName || sid,
        paymentId: params?.id || "",
        paymentTitle: customerName || "",
      }).filter((item) => item.title !== "");
    },
  },
  "/expenses/:id": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({ params, propName, customerName, searchParams }: any) => {
      const pid = searchParams?.get?.("property_id") || "";
      return buildCanonicalExpenseBreadcrumbs({
        propertyId: pid,
        propertyName: propName ?? "",
        expenseId: params?.id || "",
        expenseTitle: customerName || "",
      }).filter((item) => item.title !== "");
    },
  },
  "/estimates": {
    icon: (
      // <svg
      //   width="15"
      //   height="15"
      //   viewBox="0 0 24 24"
      //   fill="none"
      //   xmlns="http://www.w3.org/2000/svg"
      // >
      //   <path
      //     d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
      //     fill="currentColor"
      //   />
      // </svg>   <svg
      <svg
        width="13"
        height="16"
        viewBox="0 0 13 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M2.96144 8.14428H9.28856V7.26928H2.96144V8.14428ZM2.96144 10.5674H9.28856V9.69237H2.96144V10.5674ZM2.96144 12.9905H6.66356V12.1155H2.96144V12.9905ZM1.41356 15.75C1.01077 15.75 0.674479 15.6151 0.404687 15.3453C0.134896 15.0755 0 14.7392 0 14.3364V1.41356C0 1.01077 0.134896 0.674479 0.404687 0.404687C0.674479 0.134896 1.01077 0 1.41356 0H8.3125L12.25 3.9375V14.3364C12.25 14.7392 12.1151 15.0755 11.8453 15.3453C11.5755 15.6151 11.2392 15.75 10.8364 15.75H1.41356ZM7.875 4.375V0.875H1.41356C1.27881 0.875 1.15536 0.931073 1.04322 1.04322C0.931073 1.15536 0.875 1.27881 0.875 1.41356V14.3364C0.875 14.4712 0.931073 14.5946 1.04322 14.7068C1.15536 14.8189 1.27881 14.875 1.41356 14.875H10.8364C10.9712 14.875 11.0946 14.8189 11.2068 14.7068C11.3189 14.5946 11.375 14.4712 11.375 14.3364V4.375H7.875Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [{ path: "/estimates", title: "見積書一覧" }],
  },
  "/estimates/:id": {
    icon: <BreadcrumbPropertyIcon />,
    breadcrumb: ({ params, estimateTitle }: any) =>
      buildCanonicalEstimateBreadcrumbs({
        estimateId: params?.id || "",
        estimateTitle: estimateTitle || "",
      }).filter((item) => item.title !== ""),
  },

  "/setting/accout/:employee_id": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: ({ params, employeeName }: any) => [
      {
        path: `/setting/accout/${params?.employee_id}`,
        title: "プロフィール",
        clickable: false,
      },
      ...(employeeName
        ? [
            {
              path: `/setting/accout/${params?.employee_id}`,
              title: employeeName,
              truncatable: true,
            },
          ]
        : []),
    ],
  },
  "/setting/system": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [
      { path: "/setting/system", title: "設定", clickable: false },
      { path: "/setting/system", title: "書類設定", clickable: false },
    ],
  },
  "/setting/custom": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [
      { path: "/setting/custom", title: "設定", clickable: false },
      { path: "/setting/custom", title: "カスタム設定", clickable: false },
    ],
  },
  "/setting/fiscal-closing": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [
      { path: "/setting/fiscal-closing", title: "設定", clickable: false },
      { path: "/setting/fiscal-closing", title: "決算月", clickable: false },
    ],
  },
};

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  // パンくずの表示境界。sm 未満 (<600=スマホ) でのみ非表示。600 以上は表示 (サイドバー境界と統一)。
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isBelowMd = useMediaQuery(theme.breakpoints.down("md"));
  const chatPanePresentation = resolveChatPanePresentation({
    isMobile,
    isBelowMd,
  });
  const dispatch = useDispatch<AppDispatch>();
  const [logoMenuItems, setLogoMenuItems] = useState<LogoMenuItem[]>([]);
  // 右ペイン(チャット)は初期非表示。ホーム送信または再表示ボタンで開く（非スマホ）。
  const [isChatPaneOpen, setIsChatPaneOpen] = useState(DEFAULT_CHAT_PANE_OPEN);
  const [chatDisplayMode, setChatDisplayMode] =
    useState<ChatDisplayMode>("floating");
  const isChatExpanded = chatDisplayMode === "expanded";
  const {
    width: chatPaneWidth,
    handleMouseDown: handleChatPaneResizeMouseDown,
  } = useResizableChatPane();
  const handleChatExpandedChange = useCallback(
    (expanded: boolean) => {
      if (!expanded && isMobile) {
        setIsChatPaneOpen(false);
      }
    },
    [isMobile],
  );

  // クライアント設定（fiscal_closing_month 等）を起動時に取得
  useEffect(() => {
    const clientId = getClientID();
    if (clientId) {
      dispatch(fetchDetailedClient(clientId));
    }
  }, [dispatch]);

  // サイドバーの状態を取得
  const sidebarState = useSelector((state: RootState) => state.sidebar);
  const pendingChatOpen = useSelector(
    (state: RootState) => state.chat.pendingChatOpen,
  );

  // Home から送信した際、右ペインを閉じていても必ず再表示する。
  useEffect(() => {
    if (!isMobile && pendingChatOpen) {
      if (pendingChatOpen.expand) setChatDisplayMode("expanded");
      setIsChatPaneOpen(true);
    }
  }, [isMobile, pendingChatOpen]);

  // Homeへ戻った時は右チャットを閉じ、次回は通常サイズから表示する。
  useEffect(() => {
    if (shouldCloseChatPaneOnNavigation(location.pathname)) {
      setIsChatPaneOpen(false);
      setChatDisplayMode("floating");
    }
  }, [location.pathname]);

  // 従業員データを取得
  const { data: employeeData } = useSelector(
    (state: any) => state.employees.detailed,
  );

  // 物件データを取得
  const propertyData = useSelector(
    (state: any) => state.property.detailed?.data,
  );

  // 従業員名を生成
  const employeeName =
    employeeData?.first_name && employeeData?.last_name
      ? `${employeeData.last_name} ${employeeData.first_name}`
      : undefined;

  // ロゴメニューアイテムを取得
  useEffect(() => {
    const fetchLogoMenuItems = async () => {
      try {
        // クッキーから取得を試みる
        const cachedServices = getMyUseServiceCookie();
        if (cachedServices) {
          try {
            const parsed = JSON.parse(cachedServices);
            if (Array.isArray(parsed) && parsed.length > 0) {
              // キャッシュから取得したサービスを表示
              setLogoMenuItems(parsed);
              return;
            }
          } catch (error) {
            console.error("Error parsing cached services:", error);
          }
        }

        // クッキーにない場合はAPIから取得
        // ログインユーザーの割り当て済みサービスを取得
        const employeeId = getEmployeeID();
        if (!employeeId) {
          console.error("Employee ID not found");
          setLogoMenuItems([]);
          return;
        }

        const response = await ipponApi.get(
          `/api/v1/employees/${employeeId}/services?assigned_only=true`,
        );

        if (response.data && response.data.data) {
          // 現在のサイトのホスト名を取得
          const currentHostname = window.location.hostname;

          // 現在のサイトと同じドメインのものを除外
          const filteredServices = response.data.data.filter(
            (service: LogoMenuItem) => {
              try {
                const serviceUrl = new URL(service.base_url);
                const serviceHostname = serviceUrl.hostname;

                // 現在のホスト名と異なるものだけを表示
                return serviceHostname !== currentHostname;
              } catch (error) {
                // URLのパースに失敗した場合は表示しない
                console.error(
                  `Invalid URL for service ${service.name}:`,
                  service.base_url,
                );
                return false;
              }
            },
          );

          // クッキーに保存（7日間有効）
          setMyUseServiceCookie(JSON.stringify(filteredServices), 7);

          // ロゴメニューアイテムとして設定
          setLogoMenuItems(filteredServices);
        } else {
          setLogoMenuItems([]);
        }
      } catch (error) {
        console.error("Error fetching logo menu items:", error);
        setLogoMenuItems([]);
      }
    };

    fetchLogoMenuItems().then(() => {
      // ローカル開発時、サービス一覧が空の場合はモックデータを表示
      setLogoMenuItems((prev) => {
        if (prev.length > 0 || !import.meta.env.DEV) return prev;
        return [
          {
            id: "dev-sales",
            name: "売買仲介",
            base_url: "https://sales-brokerage.ippon-cloud.com",
          },
        ];
      });
    });
  }, []);

  const handleSidebarOpenChange = (open: boolean) => {
    dispatch(setSidebarOpen(open));
  };

  const handleSidebarCollapsedChange = (collapsed: boolean) => {
    dispatch(setSidebarCollapsed(collapsed));
  };

  const handleLogout = async () => {
    try {
      await dispatch(logoutAPI() as any);
    } catch (error) {
      console.error("Logout error:", error);
    }
    // 明示ログアウトでは前回画面を復元せず、次回ログイン後はホームへ移動する。
    clearPostLoginRedirect();
    navigate("/login");
  };

  const handleLogoMenuItemClick = (_url: string) => {};

  const handleBreadcrumbNavigate = (path: string) => {
    const currentState = location.state as any;
    if (currentState) {
      if (path.startsWith("/repairs/") && path !== "/repairs") {
        navigate(path, {
          state: {
            propName: currentState.propName,
            customerName: currentState.repairTitle || currentState.customerName,
            propertyId: currentState.propertyId,
          },
        });
        return;
      }
      if (
        path.startsWith("/properties/") &&
        path !== "/properties" &&
        currentState.propName
      ) {
        const navState: Record<string, unknown> = {
          propName: currentState.propName,
        };
        // 区画詳細 (/units/:id) へ戻る場合
        if (path.includes("/units/")) {
          if (currentState.privateAreaName) {
            navState.customerName = currentState.privateAreaName;
            navState.privateAreaName = currentState.privateAreaName;
          } else if (currentState.commonAreaName) {
            navState.customerName = currentState.commonAreaName;
          }
          if (currentState.fromSource) {
            navState.fromSource = currentState.fromSource;
          }
        }
        navigate(path, { state: navState });
        return;
      }
    }
    navigate(path);
  };

  const currentTab = searchParams.get("tab");
  const currentSubtab = searchParams.get("subtab");

  const headerProps = useMemo(() => {
    const currentPropName =
      (location.state as any)?.propName ?? propertyData?.name;

    return {
      fullName: getFullName() || "",
      employeeId: getEmployeeID() || "",
      onLogout: handleLogout,
      showUserInfo: true,
      profileImage: profileImg,
      breadcrumb: !isMobile ? (
        <Breadcrumb
          key={`${currentPropName ?? "initial"}-${currentTab ?? "no-tab"}-${currentSubtab ?? "no-subtab"}`}
          routeConfig={routeConfig}
          propName={currentPropName}
          customerName={
            (location.state as any)?.title ||
            (location.state as any)?.customerName ||
            (location.state as any)?.repair?.title ||
            (location.state as any)?.estimate?.title ||
            (location.state as any)?.privateAreaName ||
            (location.state as any)?.ancillaryName
          }
          employeeName={employeeName}
          customParams={{
            repairTitle: (location.state as any)?.repairTitle,
            estimateTitle:
              (location.state as any)?.estimateTitle ||
              (location.state as any)?.title,
            repairId: (location.state as any)?.repairId,
            propertyId: (location.state as any)?.propertyId,
            fromEstimateList: (location.state as any)?.fromEstimateList,
            fromSource: (location.state as any)?.fromSource,
            privateAreaId: (location.state as any)?.privateAreaId,
            privateAreaName: (location.state as any)?.privateAreaName,
            commonAreaId: (location.state as any)?.commonAreaId,
            commonAreaName: (location.state as any)?.commonAreaName,
            searchParams: searchParams,
          }}
          onNavigate={handleBreadcrumbNavigate}
        />
      ) : null,
      defaultMenuItems: true,
      showUserMenu: true,
      onNavigate: handleBreadcrumbNavigate,
    } as any;
  }, [
    location.pathname,
    location.state,
    currentTab,
    currentSubtab,
    isMobile,
    propertyData?.name,
    employeeName,
    navigate,
  ]);

  return (
    <>
      <DashboardLayout
        showMobileHeader={true}
        logo={null}
        externalSidebarOpen={sidebarState.isOpen}
        externalForceCollapsed={sidebarState.forceCollapsed}
        onSidebarOpenChange={handleSidebarOpenChange}
        onSidebarCollapsedChange={handleSidebarCollapsedChange}
        // 物件詳細＞以降のページは左 padding 32px、それ以外は 24px
        contentPl={
          /^\/properties\/(?!create$|search$)/.test(location.pathname)
            ? { lg: 4, xs: 3 }
            : { lg: 3, xs: 3 }
        }
        headerProps={headerProps}
        // 900px以上ではメイン領域と並べる常設チャット。
        rightPane={
          shouldMountDesktopChatPane({
            isOpen: isChatPaneOpen,
            isExpanded: isChatExpanded,
            presentation: chatPanePresentation,
          }) ? (
            <ChatRightPane
              onClose={() => {
                setIsChatPaneOpen(false);
                setChatDisplayMode("floating");
              }}
              onExpandedChange={handleChatExpandedChange}
              onModeChange={setChatDisplayMode}
              onResizeMouseDown={handleChatPaneResizeMouseDown}
              paneWidth={chatPaneWidth}
              initialMode="floating"
              openOrigin={
                typeof pendingChatOpen?.originX === "number" &&
                typeof pendingChatOpen.originY === "number"
                  ? {
                      x: pendingChatOpen.originX,
                      y: pendingChatOpen.originY,
                    }
                  : undefined
              }
              initiallyExpanded={pendingChatOpen?.expand === true}
            />
          ) : undefined
        }
        rightPaneWidth={isChatPaneOpen ? chatPaneWidth : 0}
        rightPaneOverlay={
          chatPanePresentation !== "docked" || chatDisplayMode !== "docked"
        }
        renderRightPaneOnPhone={isChatExpanded}
        sidebarProps={{
          menus: sidebarMenus,
          bottomMenus: bottomMenus,
          currentPath: location.pathname,
          logoMenuItems: logoMenuItems,
          onLogoMenuItemClick: handleLogoMenuItemClick,
          onNavigate: (path: string) => {
            console.log("Navigating to:", path);
            navigate(path);
          },
          onMenuItemClick: (menuId: string, subItemTo?: string) => {
            console.log("Menu item clicked:", menuId, "subItem:", subItemTo);
          },
          ...getSidebarBrandLogoNodes({
            label: "物件管理",
            logoSrc: logosPng,
            logoCollapsedSrc: ipponTradingLogoSpXs,
          }),
          theme: {
            primaryColor: "#0E9DBC",
            hoverColor: "#DBF8FF",
            activeColor: "#0e9dbc",
          },
        }}
      >
        <Outlet />
      </DashboardLayout>
      {/* スマホは右ペインを出せないので、従来のフローティングチャットを使う。 */}
      {isMobile && !isChatExpanded && <AIChatButton />}
      {/* デスクトップで右ペインを閉じている時の再表示ボタン（ホームを除く）。 */}
      {shouldShowChatReopenButton({
        isMobile,
        isChatPaneOpen,
        pathname: location.pathname,
      }) && (
        <Box sx={{ position: "fixed", bottom: 24, right: 24, zIndex: 1300 }}>
          <Tooltip title="AIチャットを開く" placement="left">
            <Fab
              aria-label="AIチャットを開く"
              onClick={() => setIsChatPaneOpen(true)}
              sx={{
                width: 56,
                height: 56,
                backgroundColor: "#0E9DBC",
                color: "#FFFFFF",
                boxShadow: "0px 4px 12px rgba(14, 157, 188, 0.3)",
                "&:hover": { backgroundColor: "#0A8BA8" },
              }}
            >
              <AutoAwesomeOutlinedIcon />
            </Fab>
          </Tooltip>
        </Box>
      )}
    </>
  );
};

export default MainLayout;
